import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface TeamMember {
  id: string;
  account_id: string;
  auth_user_id?: string | null;
  email: string;
  name: string | null;
  phone: string | null;
  role: string | null;
  is_active: boolean;
  order_index: number;
  view_all_contacts?: boolean | null;
  can_assign_contacts?: boolean;
  view_unassigned_contacts?: boolean;
  created_at: string;
  updated_at: string;
}

export function useTeam() {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!user?.account?.id || !supabase) {
      console.log('❌ [useTeam] fetchMembers: Missing requirements', {
        hasUser: !!user,
        hasAccount: !!user?.account,
        accountId: user?.account?.id,
        hasSupabase: !!supabase
      });
      return;
    }

    console.log('🔍 [useTeam] fetchMembers: Starting fetch', {
      accountId: user.account.id,
      userEmail: user.email,
      userName: user.name,
      authUserId: (user as any).auth_user_id,
      userType: (user as any).type
    });

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_users')
        .select('*')
        .eq('account_id', user.account.id)
        .order('order_index', { ascending: true });

      console.log('📊 [useTeam] fetchMembers: Query result', {
        success: !fetchError,
        count: data?.length || 0,
        error: fetchError?.message,
        errorCode: fetchError?.code,
        errorDetails: fetchError?.details
      });

      if (fetchError) {
        console.error('❌ [useTeam] fetchMembers: Database error', {
          message: fetchError.message,
          code: fetchError.code,
          details: fetchError.details,
          hint: fetchError.hint
        });
        throw fetchError;
      }

      console.log('✅ [useTeam] fetchMembers: Success -', data?.length || 0, 'members loaded');
      setMembers(data || []);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao buscar membros da equipe';
      setError(errorMessage);
      console.error('❌ [useTeam] fetchMembers: Catch error', {
        message: err.message,
        code: err.code,
        details: err.details,
        stack: err.stack
      });
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id, user?.email, user?.name]);

  const createMember = async (member: Omit<TeamMember, 'id' | 'account_id' | 'created_at' | 'updated_at' | 'auth_user_id' | 'order_index'> & { password: string; send_email?: boolean; view_all_contacts?: boolean; can_assign_contacts?: boolean; view_unassigned_contacts?: boolean }) => {
    if (!user?.account?.id || !supabase) {
      setError('Usuário ou conta não encontrada');
      return null;
    }

    try {
      setError(null);
      setLoading(true);

      console.log('📤 Calling create-team-member Edge Function');

      const requestBody = {
        account_id: user.account.id,
        email: member.email,
        password: member.password,
        name: member.name,
        phone: (member as any).phone || null,
        role: member.role,
        is_active: member.is_active,
        send_email: member.send_email !== undefined ? member.send_email : true,
        view_all_contacts: member.view_all_contacts ?? false,
        can_assign_contacts: member.can_assign_contacts ?? false,
        view_unassigned_contacts: member.view_unassigned_contacts ?? false
      };
      console.log('📦 Request body:', { ...requestBody, password: '***' });

      const { data: response, error: invokeError } = await supabase.functions.invoke('create-team-member', {
        body: requestBody
      });

      console.log('📥 Full response:', response);
      console.log('📥 Invoke error:', invokeError);

      if (invokeError) {
        console.error('❌ Edge Function error:', invokeError);
        const errorDetail = response?.error || invokeError.message;
        throw new Error(errorDetail);
      }

      if (!response?.success) {
        console.error('❌ Response error:', response?.error, response);
        throw new Error(response?.error || 'Erro desconhecido ao criar membro');
      }

      console.log('✅ Team member created:', response.data);

      setMembers(prev => [response.data, ...prev]);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao criar membro';
      setError(errorMessage);
      console.error('Error creating team member:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateMember = async (id: string, updates: Partial<TeamMember>) => {
    if (!supabase) {
      setError('Supabase não configurado');
      return false;
    }

    try {
      setError(null);
      setLoading(true);

      console.log('📤 Calling update-team-member Edge Function');

      const { data: response, error: invokeError } = await supabase.functions.invoke('update-team-member', {
        body: {
          member_id: id,
          name: updates.name,
          phone: updates.phone,
          role: updates.role,
          is_active: updates.is_active,
          view_all_contacts: updates.view_all_contacts,
          can_assign_contacts: updates.can_assign_contacts,
          view_unassigned_contacts: updates.view_unassigned_contacts
        }
      });

      if (invokeError) {
        console.error('❌ Edge Function error:', invokeError);
        throw new Error(invokeError.message);
      }

      if (!response.success) {
        console.error('❌ Response error:', response.error);
        throw new Error(response.error);
      }

      console.log('✅ Team member updated:', response.data);

      setMembers(prev => prev.map(m =>
        m.id === id ? response.data : m
      ));

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao atualizar membro';
      setError(errorMessage);
      console.error('Error updating team member:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id: string) => {
    if (!supabase) {
      setError('Supabase não configurado');
      return false;
    }

    try {
      setError(null);
      setLoading(true);

      console.log('📤 Calling delete-team-member Edge Function');

      const { data: response, error: invokeError } = await supabase.functions.invoke('delete-team-member', {
        body: {
          member_id: id
        }
      });

      if (invokeError) {
        console.error('❌ Edge Function error:', invokeError);
        throw new Error(invokeError.message);
      }

      if (!response.success) {
        console.error('❌ Response error:', response.error);
        throw new Error(response.error);
      }

      console.log('✅ Team member deleted');

      setMembers(prev => prev.filter(m => m.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao deletar membro';
      setError(errorMessage);
      console.error('Error deleting team member:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reorderMembers = async (newOrder: TeamMember[]) => {
    if (!user?.account?.id || !supabase) {
      setError('Usuário ou conta não encontrada');
      return false;
    }

    try {
      setError(null);

      // Optimistic update
      const previousMembers = [...members];
      setMembers(newOrder);

      console.log('📤 Calling reorder-team-members Edge Function');

      const member_orders = newOrder.map((member, index) => ({
        id: member.id,
        order_index: index + 1
      }));

      const { data: response, error: invokeError } = await supabase.functions.invoke('reorder-team-members', {
        body: {
          account_id: user.account.id,
          member_orders
        }
      });

      if (invokeError) {
        console.error('❌ Edge Function error:', invokeError);
        setMembers(previousMembers);
        throw new Error(invokeError.message);
      }

      if (!response.success) {
        console.error('❌ Response error:', response.error);
        setMembers(previousMembers);
        throw new Error(response.error);
      }

      console.log('✅ Team members reordered:', response.data);
      setMembers(response.data);
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao reordenar membros';
      setError(errorMessage);
      console.error('Error reordering team members:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return {
    members,
    loading,
    error,
    fetchMembers,
    createMember,
    updateMember,
    deleteMember,
    reorderMembers
  };
}
